// Mock data for periods and day structures - SAT V4 Zone
import type {
  DayOfWeek,
  DaySessionsMap,
  DayStructure,
  DayWeekFormatData,
  LunchDaySessionsMap,
  LunchSession,
  LunchStructureData,
  Period,
  Session,
  YearGroup,
  YearGroupString,
} from '../../types';

const days: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// All year groups constant
const ALL_YEAR_GROUPS: YearGroup[] = [7, 8, 9, 10, 11];

// Helper to calculate end time
const calculateEndTime = (startTime: string, durationMinutes: number): string => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + durationMinutes;
  const endHours = Math.floor(totalMinutes / 60);
  const endMinutes = totalMinutes % 60;
  return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
};

// ============================================
// Legacy Period Data (for backwards compatibility)
// ============================================

// Monday standard day structure with year group variations
// Based on the pattern: Yrs 7, 8, & 11 have Lunch before Period 4
// Yrs 9 & 10 have Period 4 before Lunch
const mondayPeriods: Omit<Period, 'id' | 'dayOfWeek'>[] = [
  {
    name: 'Registration',
    type: 'tutor',
    startTime: '08:45',
    endTime: '09:00',
    duration: 15,
    order: 0,
    yearGroups: ALL_YEAR_GROUPS,
  },
  {
    name: 'Period 1',
    type: 'lesson',
    startTime: '09:00',
    endTime: '10:00',
    duration: 60,
    order: 1,
    yearGroups: ALL_YEAR_GROUPS,
  },
  {
    name: 'Period 2',
    type: 'lesson',
    startTime: '10:00',
    endTime: '11:00',
    duration: 60,
    order: 2,
    yearGroups: ALL_YEAR_GROUPS,
  },
  {
    name: 'Break',
    type: 'break',
    startTime: '11:00',
    endTime: '11:15',
    duration: 15,
    order: 3,
    yearGroups: ALL_YEAR_GROUPS,
  },
  {
    name: 'Period 3',
    type: 'lesson',
    startTime: '11:15',
    endTime: '12:15',
    duration: 60,
    order: 4,
    yearGroups: ALL_YEAR_GROUPS,
  },
  // Lunch for Years 7, 8 & 11 (order 5)
  {
    name: 'Lunch',
    type: 'lunch',
    startTime: '12:15',
    endTime: '13:00',
    duration: 45,
    order: 5,
    yearGroups: [7, 8, 11],
  },
  // Period 4 for Years 9 & 10 (same time slot as lunch for 7,8,11)
  {
    name: 'Period 4',
    type: 'lesson',
    startTime: '12:15',
    endTime: '13:00',
    duration: 45,
    order: 5,
    yearGroups: [9, 10],
  },
  // Period 4 for Years 7, 8 & 11 (order 6)
  {
    name: 'Period 4',
    type: 'lesson',
    startTime: '13:00',
    endTime: '14:00',
    duration: 60,
    order: 6,
    yearGroups: [7, 8, 11],
  },
  // Lunch for Years 9 & 10 (same time slot as period 4 for 7,8,11)
  { name: 'Lunch', type: 'lunch', startTime: '13:00', endTime: '13:45', duration: 45, order: 6, yearGroups: [9, 10] },
  {
    name: 'Period 5',
    type: 'lesson',
    startTime: '14:00',
    endTime: '15:00',
    duration: 60,
    order: 7,
    yearGroups: ALL_YEAR_GROUPS,
  },
];

// Create periods - only Monday has data, others are empty
export const mockPeriods: Period[] = mondayPeriods.map((p, idx) => ({
  ...p,
  id: `monday-p${idx}`,
  dayOfWeek: 'monday',
}));

export const mockDayStructures: DayStructure[] = days.map((day) => ({
  id: `day-${day}`,
  dayOfWeek: day,
  periods: mockPeriods.filter((p) => p.dayOfWeek === day),
}));

// Helper to get lesson periods only (for timetable generation)
export const lessonPeriods = mockPeriods.filter((p) => p.type === 'lesson');

// ============================================
// New Session Data Structure
// ============================================

// Year 7 Monday sessions
const createYear7MondaySessions = (dayPrefix: string): Session[] => [
  {
    id: `${dayPrefix}-s0`,
    sessionOrder: 0,
    session: ['Registration'],
    startTime: '08:45',
    duration: 15,
    endTime: calculateEndTime('08:45', 15),
  },
  {
    id: `${dayPrefix}-s1`,
    sessionOrder: 1,
    session: ['Period'],
    periodNumber: 1,
    startTime: '09:00',
    duration: 60,
    endTime: calculateEndTime('09:00', 60),
  },
  {
    id: `${dayPrefix}-s2`,
    sessionOrder: 2,
    session: ['Period'],
    periodNumber: 2,
    startTime: '10:00',
    duration: 60,
    endTime: calculateEndTime('10:00', 60),
  },
  {
    id: `${dayPrefix}-s3`,
    sessionOrder: 3,
    session: ['Break'],
    startTime: '11:00',
    duration: 15,
    endTime: calculateEndTime('11:00', 15),
  },
  {
    id: `${dayPrefix}-s4`,
    sessionOrder: 4,
    session: ['Period'],
    periodNumber: 3,
    startTime: '11:15',
    duration: 60,
    endTime: calculateEndTime('11:15', 60),
  },
  {
    id: `${dayPrefix}-s5`,
    sessionOrder: 5,
    session: ['Period', 'Lunch'],
    periodNumber: 4,
    startTime: '12:15',
    duration: 105,
    endTime: calculateEndTime('12:15', 105),
  },
  {
    id: `${dayPrefix}-s6`,
    sessionOrder: 6,
    session: ['Period'],
    periodNumber: 5,
    startTime: '14:00',
    duration: 60,
    endTime: calculateEndTime('14:00', 60),
  },
];

// Empty sessions for all other days
const createEmptyDaySessions = (): Session[] => [];

// Empty DaySessionsMap (no sessions)
const createEmptyDaySessionsMap = (): DaySessionsMap => ({
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
});

// Create DaySessionsMap with only Monday data for Year 7
const createYear7DaySessionsMap = (weekSuffix: string): DaySessionsMap => ({
  monday: createYear7MondaySessions(`Year7-${weekSuffix}-mon`),
  tuesday: createEmptyDaySessions(),
  wednesday: createEmptyDaySessions(),
  thursday: createEmptyDaySessions(),
  friday: createEmptyDaySessions(),
  saturday: createEmptyDaySessions(),
  sunday: createEmptyDaySessions(),
});

// Create Year 7 Schedule Data (only Monday has data)
const createYear7ScheduleData = () => ({
  'Bi-Weekly': {
    'Week-A': createYear7DaySessionsMap('A'),
    'Week-B': createEmptyDaySessionsMap(),
  },
  Weekly: createYear7DaySessionsMap('W'),
});

// Create empty Year Group Schedule Data (for Year 8-11)
const createEmptyYearGroupScheduleData = () => ({
  'Bi-Weekly': {
    'Week-A': createEmptyDaySessionsMap(),
    'Week-B': createEmptyDaySessionsMap(),
  },
  Weekly: createEmptyDaySessionsMap(),
});

// Day/Week Format Data - only Year 7/Monday has data
export const mockDayWeekFormatData: DayWeekFormatData = {
  'Year 7': createYear7ScheduleData(),
  'Year 8': createEmptyYearGroupScheduleData(),
  'Year 9': createEmptyYearGroupScheduleData(),
  'Year 10': createEmptyYearGroupScheduleData(),
  'Year 11': createEmptyYearGroupScheduleData(),
};

// ============================================
// Lunch Structure Data
// ============================================

// Create Lunch Sessions with year groups - 6 records for split lunch structure
const createLunchMondaySessions = (dayPrefix: string): LunchSession[] => [
  {
    id: `${dayPrefix}-ls0`,
    sessionOrder: 0,
    session: ['Lunch'],
    startTime: '12:15',
    duration: 45,
    endTime: calculateEndTime('12:15', 45),
    yearGroups: ['Year 7', 'Year 8', 'Year 9'],
  },
  {
    id: `${dayPrefix}-ls1`,
    sessionOrder: 1,
    session: ['Period'],
    periodNumber: 4,
    startTime: '13:00',
    duration: 45,
    endTime: calculateEndTime('13:00', 45),
    yearGroups: ['Year 7', 'Year 8', 'Year 9'],
  },
  {
    id: `${dayPrefix}-ls2`,
    sessionOrder: 2,
    session: ['Period'],
    periodNumber: 5,
    startTime: '13:45',
    duration: 60,
    endTime: calculateEndTime('13:45', 60),
    yearGroups: ['Year 7', 'Year 8', 'Year 9'],
  },
  {
    id: `${dayPrefix}-ls3`,
    sessionOrder: 3,
    session: ['Period'],
    periodNumber: 4,
    startTime: '12:15',
    duration: 45,
    endTime: calculateEndTime('12:15', 45),
    yearGroups: ['Year 10', 'Year 11'],
  },
  {
    id: `${dayPrefix}-ls4`,
    sessionOrder: 4,
    session: ['Period'],
    periodNumber: 5,
    startTime: '13:00',
    duration: 60,
    endTime: calculateEndTime('13:00', 60),
    yearGroups: ['Year 10', 'Year 11'],
  },
  {
    id: `${dayPrefix}-ls5`,
    sessionOrder: 5,
    session: ['Lunch'],
    startTime: '14:00',
    duration: 45,
    endTime: calculateEndTime('14:00', 45),
    yearGroups: ['Year 10', 'Year 11'],
  },
];

const createEmptyLunchDaySessions = (): LunchSession[] => [];

// Empty LunchDaySessionsMap
const createEmptyLunchDaySessionsMap = (): LunchDaySessionsMap => ({
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
});

// Create LunchDaySessionsMap with only Monday data
const createLunchDaySessionsMap = (weekSuffix: string): LunchDaySessionsMap => ({
  monday: createLunchMondaySessions(`${weekSuffix}-mon`),
  tuesday: createEmptyLunchDaySessions(),
  wednesday: createEmptyLunchDaySessions(),
  thursday: createEmptyLunchDaySessions(),
  friday: createEmptyLunchDaySessions(),
  saturday: createEmptyLunchDaySessions(),
  sunday: createEmptyLunchDaySessions(),
});

// Lunch Structure Data - only Bi-Weekly/Week-A/Monday and Weekly/Monday have data
export const mockLunchStructureData: LunchStructureData = {
  'Bi-Weekly': {
    'Week-A': createLunchDaySessionsMap('A'),
    'Week-B': createEmptyLunchDaySessionsMap(),
  },
  Weekly: createLunchDaySessionsMap('W'),
};

export default mockPeriods;
